import { getRouteApi } from '@tanstack/react-router'
import { takeRight } from 'lodash'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebounceValue } from 'usehooks-ts'

import AddIcon from '@/assets/icons/add.svg?react'
import Cancel from '@/assets/icons/cancel.svg?react'
import EditIcon from '@/assets/icons/edit.svg?react'
import HistoryIcon from '@/assets/icons/history.svg?react'
import SendLinkIcon from '@/assets/icons/send-link.svg?react'
import Unlock from '@/assets/icons/unlock.svg?react'
import Button from '@/components/Button/Button'
import ConfirmModal from '@/components/ConfirmModal'
import LinkText from '@/components/LinkText/LinkText'
import type { SelectOption } from '@/components/Select/Select'
import Table, { type TableAction } from '@/components/Table/Table'
import { TABLE_CELL_WIDTH } from '@/components/Table/table-cell-width'
import TableTopBarActions from '@/components/TableTopBar/TableTopBarActions'
import Tag from '@/components/Tag/Tag'
import Tooltip from '@/components/Tooltip/Tooltip'
import ExportButton from '@/components/common/ExportButton'
import Filters, { type Filter } from '@/components/common/Filters/Filters'
import TruncateElementsList from '@/components/common/TruncateElementsList'
import DEFAULT_PAGE_SIZE from '@/constants/default-page-size'
import { useColumns } from '@/hooks/useColumns'
import { useDebounceSearch } from '@/hooks/useDebounceSearch'
import { usePagination } from '@/hooks/usePagination'
import useRowSelection from '@/hooks/useRowSelection'
import { useScreenResolution } from '@/hooks/useScreenResolution'
import { toast } from '@/hooks/useToast'
import useStudents, { type Student } from '@/queries/useStudents'
import useAuthStore from '@/store/useAuthStore'
import { type UserStatus } from '@/types/user-status'
import { addUnassignedFilterOption } from '@/utils/add-unassigned-filter-option'
import { getColumnWidth } from '@/utils/get-column-width'
import { getFilterValue } from '@/utils/get-filter-value'
import { getUserStatusTag } from '@/utils/get-user-status-tag'
import { getValue } from '@/utils/get-value'
import { mergeOptions } from '@/utils/merge-options'

import styles from './StudentsView.module.scss'
import ActivationLinkModal, {
  type ActivationLinkModalVariant
} from '../components/ActivationLinkModal'
import ArchiveStudentModal from '../components/ArchiveStudentModal'
import type { StudentsFiltersKey } from '../constants/students-filters'
import useActivateStudent from '../mutations/useActivateStudent'
import useBlockStudent from '../mutations/useBlockStudent'
import useStudentsDownload from '../mutations/useStudentsDownload'
import useStudentsAll from '../queries/useStudentsAll'
import useStudentsAllCount from '../queries/useStudentsAllCount'
import { useStudentFilters } from '../queries/useStudentsFilters'
import { getStudentStatusTootlipText } from '../utils/statuses'

const routeApi = getRouteApi('/_auth/students-and-classes/students/')

const StudentsView = () => {
  const { t } = useTranslation('students')

  const { user } = useAuthStore()

  const {
    page,
    pageSize,
    search,
    academicLevelId,
    classId,
    tutor,
    parent,
    status
  } = routeApi.useSearch()
  const navigate = routeApi.useNavigate()

  const [openBlockConfirmModal, setOpenBlockConfirmModal] = useState(false)
  const [openActivateConfirmModal, setOpenActivateConfirmModal] =
    useState(false)
  const [currentStudent, setCurrentStudent] = useState<Student | null>()
  const [openActivationLinkModal, setOpenActivationLinkModal] = useState(false)
  const [activationLinkModalVariant, setActivationLinkModalVariant] =
    useState<ActivationLinkModalVariant>('bulk')
  const [openBulkArchiveConfirmModal, setOpenBulkArchiveConfirmModal] =
    useState(false)

  const { searchValue, handleChangeSearch } = useDebounceSearch(routeApi)

  const [classSearch, setClassSearch] = useDebounceValue('', 200)
  const [parentSearch, setParentSearch] = useDebounceValue('', 200)
  const [tutorSearch, setTutorSearch] = useDebounceValue('', 200)

  const { isMobile, isExtraLarge } = useScreenResolution()

  const requestParams = {
    search,
    academicLevelId,
    tutor: tutor?.map(getValue),
    parent: parent?.map(getValue),
    status,
    classId: classId?.map(getValue)
  }

  const {
    data: students,
    isFetching: isStudentsLoading,
    refetch
  } = useStudents({
    pageSize,
    page,
    ...requestParams
  })

  const { data: allStudents } = useStudentsAll(requestParams)

  const { data: allStudentCount } = useStudentsAllCount()

  const {
    classesQuery,
    parentQuery,
    tutorsQuery,
    academicLevelsQuery,
    statusQuery
  } = useStudentFilters({
    class: { search: classSearch },
    parent: { search: parentSearch },
    tutor: { search: tutorSearch }
  })

  const changeFilter = (
    key: StudentsFiltersKey,
    value?: string | string[] | SelectOption<string>[]
  ) => {
    navigate({
      search: previousValue => ({
        ...previousValue,
        page: 1,
        [key]: getFilterValue(value)
      })
    })
  }

  const handleClearAll = () => {
    navigate({
      search: {
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE
      }
    })
  }

  const selection = useRowSelection({
    count: allStudentCount || 0,
    itemsToSelect: allStudents?.list || []
  })

  const closeBlockConfirmModal = () => {
    refetch()
    setOpenBlockConfirmModal(false)
    setCurrentStudent(null)
  }

  const { mutate: blockStudent } = useBlockStudent({
    onSuccess: () => {
      toast({
        variant: 'success',
        title: t('toast.student-blocked', { NAME: currentStudent?.fullName })
      })
      closeBlockConfirmModal()
    },
    onError: () => {
      toast({
        variant: 'error',
        title: t('toast.student-blocked-failed', {
          NAME: currentStudent?.fullName
        })
      })
      closeBlockConfirmModal()
    }
  })

  const closeActivateConfirmModal = () => {
    refetch()
    setOpenActivateConfirmModal(false)
    setCurrentStudent(null)
  }

  const { mutate: activateStudent } = useActivateStudent({
    onSuccess: () => {
      toast({
        variant: 'success',
        title: t('toast.student-activate', {
          NAME: currentStudent?.fullName
        })
      })
      closeActivateConfirmModal()
    },
    onError: () => {
      toast({
        variant: 'error',
        title: t('toast.student-activate', {
          NAME: currentStudent?.fullName
        })
      })
      closeActivateConfirmModal()
    }
  })

  const handleBulkArchive = () => {
    selection.clearSelection()
    refetch()
  }

  const { columns: tableColumns, ...manageColumns } = useColumns<Student>(
    [
      {
        key: 'full_name',
        label: t('header.student'),
        cellDataTestId: 'student',
        width: isMobile ? TABLE_CELL_WIDTH.XL : undefined,
        render: (value: Student) => (
          <LinkText
            to="/students-and-classes/students/$studentId/details"
            params={{
              studentId: value.id
            }}
          >
            {value.fullName}
          </LinkText>
        )
      },
      {
        dataIndex: ['academicLevel', 'name'],
        key: 'academic_level',
        label: t('header.academic-level'),
        cellDataTestId: 'academic-level',
        width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.MD)
      },
      {
        key: 'student_class',
        label: t('header.class'),
        cellDataTestId: 'class-name',
        width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.MD),
        render: (value: Student) =>
          value.studentClass ? (
            <LinkText
              to="/students-and-classes/classes/$classId/details"
              params={{
                classId: value.studentClass.id
              }}
            >
              <div data-test-id="class">{value.studentClass.name}</div>
            </LinkText>
          ) : (
            <span className={styles.textUnassigned}>
              {t('text.unassigned')}
            </span>
          )
      },
      {
        cellDataTestId: 'tutor',
        key: 'tutor',
        width: isMobile ? TABLE_CELL_WIDTH.XL : undefined,
        label: t('header.tutor'),
        render: (value: Student) =>
          value.tutor ? (
            <LinkText
              to="/users/$userId/details"
              params={{
                userId: value.tutor.id
              }}
            >
              {value.tutor.fullName}
            </LinkText>
          ) : (
            <span className={styles.textUnassigned}>
              {t('text.unassigned')}
            </span>
          )
      },
      {
        dataIndex: 'parents',
        cellDataTestId: 'parents',
        key: 'parents',
        label: t('header.guardians'),
        width: isMobile ? TABLE_CELL_WIDTH.XL : undefined,
        render: value => <ParentsColumn parents={value} />
      },
      {
        dataIndex: 'status',
        key: 'status',
        cellDataTestId: 'status',
        label: t('header.status'),
        width: 100,
        render: (value: UserStatus) => (
          <Tooltip
            trigger={<Tag {...getUserStatusTag(value)} />}
            text={getStudentStatusTootlipText(value)}
          />
        )
      }
    ],
    { routeApi }
  )

  const pagination = usePagination(routeApi)

  const handleBlock = () => {
    if (currentStudent) blockStudent({ id: currentStudent?.id })
  }

  const handleActivate = () => {
    if (currentStudent) activateStudent({ id: currentStudent?.id })
  }

  const actions: (row: Student) => TableAction<Student>[] = row =>
    user?.isSuperAdmin
      ? [
          {
            icon: <EditIcon />,
            text: t('button.edit'),
            onClick: (item: Student) => {
              navigate({
                to: '/students-and-classes/students/edit/$studentId',
                params: { studentId: item.id }
              })
            }
          },
          {
            icon: <SendLinkIcon />,
            text:
              row.status === 'active'
                ? t('button.resend-activation-link')
                : t('button.send-activation-link'),
            hidden: row.status === 'blocked',
            onClick: (item: Student) => {
              setCurrentStudent(item)
              setActivationLinkModalVariant('single')
              setOpenActivationLinkModal(true)
            }
          },
          {
            icon: <Cancel />,
            text: t('button.block-student'),
            hidden: row.status === 'blocked' || row.status === 'inactive',
            onClick: (item: Student) => {
              setCurrentStudent(item)
              setOpenBlockConfirmModal(true)
            }
          },
          {
            icon: <Unlock />,
            text: t('button.activate-student'),
            hidden: row.status === 'active' || row.status === 'inactive',
            onClick: (item: Student) => {
              setCurrentStudent(item)
              setOpenActivateConfirmModal(true)
            }
          },
          {
            icon: <HistoryIcon />,
            text: t('button.archive-student'),
            hidden: row.id === user.id,
            onClick: (item: Student) => {
              setCurrentStudent(item)
              setOpenBulkArchiveConfirmModal(true)
            }
          }
        ].filter(item => !item.hidden)
      : []

  const filters: Filter[] = [
    {
      label: t('label.search'),
      variant: 'input',
      filterProps: {
        id: 'search',
        value: searchValue,
        type: 'search',
        dataTestId: 'search',
        placeholder: t('label.student'),
        onChange: value => handleChangeSearch(value)
      }
    },
    {
      label: t('label.academic-level'),
      variant: 'multiselect',
      filterProps: {
        id: 'academicLevelId',
        multiple: true,
        loading: academicLevelsQuery.isLoading,
        options: academicLevelsQuery?.data || [],
        dataTestId: 'academic-level-filter',
        value: academicLevelId,
        placeholder: t('label.academic-level'),
        onChange: value => changeFilter('academicLevelId', value)
      }
    },
    {
      label: t('label.class'),
      variant: 'multiselect',
      filterProps: {
        id: 'class',
        multiple: true,
        mode: 'detailed',
        loading: classesQuery.isLoading,
        options: addUnassignedFilterOption(
          mergeOptions(
            classesQuery.data?.pages.flatMap(({ results }) => results),
            classId
          )
        ),
        onInputChange: setClassSearch,
        onMenuScrollToBottom: () => classesQuery.fetchNextPage(),
        value: classId,
        placeholder: t('label.class'),
        onChange: value => changeFilter('classId', value)
      }
    },
    {
      label: t('label.tutor'),
      variant: 'multiselect',
      filterProps: {
        id: 'tutor',
        multiple: true,
        mode: 'detailed',
        dataTestId: 'tutor-filter',
        loading: tutorsQuery.isLoading,
        options: addUnassignedFilterOption(
          mergeOptions(
            tutorsQuery.data?.pages.flatMap(({ results }) => results),
            tutor
          )
        ),
        onInputChange: setTutorSearch,
        onMenuScrollToBottom: () => tutorsQuery.fetchNextPage(),
        value: tutor,
        placeholder: t('label.tutor'),
        onChange: value => changeFilter('tutor', value)
      }
    },
    {
      label: t('label.guardians'),
      variant: 'multiselect',
      filterProps: {
        id: 'parent',
        multiple: true,
        mode: 'detailed',
        loading: parentQuery.isLoading,
        options: addUnassignedFilterOption(
          mergeOptions(
            parentQuery.data?.pages.flatMap(({ results }) => results),
            parent
          )
        ),
        onInputChange: setParentSearch,
        onMenuScrollToBottom: () => parentQuery.fetchNextPage(),
        value: parent,
        placeholder: t('label.guardians'),
        onChange: value => changeFilter('parent', value)
      }
    },
    {
      label: t('label.status'),
      variant: 'multiselect',
      filterProps: {
        id: 'status',
        multiple: true,
        loading: statusQuery.isLoading,
        options: statusQuery.data || [],
        value: status,
        placeholder: t('label.status'),
        onChange: value => changeFilter('status', value)
      }
    }
  ]

  const isSomeFilterSelected = filters.some(filter => filter.filterProps.value)

  const bulkActions = [
    {
      text: t('button.send-activation-link'),
      icon: <SendLinkIcon />,
      onClick: () => {
        setActivationLinkModalVariant('bulk')
        setOpenActivationLinkModal(true)
      }
    },
    {
      text: t('button.archive-students'),
      icon: <HistoryIcon />,
      onClick: () => setOpenBulkArchiveConfirmModal(true)
    }
  ]
  const download = useStudentsDownload({
    ...requestParams,
    columnsKey: manageColumns.activeColumnKeys
  })

  return (
    <>
      <Filters
        filters={filters}
        onClearAll={handleClearAll}
        disabledClearAllButton={!isSomeFilterSelected}
        actionsRender={
          <>
            <ExportButton {...download} />

            {user?.isSuperAdmin ? (
              <Button
                asLink
                to="/students-and-classes/students/add"
                icon={<AddIcon />}
              >
                {t('button.add-new-student')}
              </Button>
            ) : null}
          </>
        }
        manageColumns={manageColumns}
      />
      <Table
        id="students-table"
        isLoading={isStudentsLoading}
        actions={actions}
        rowSelection={
          user?.isSuperAdmin
            ? {
                selectedItems: selection.selectedItems,
                allCheckboxValue: selection.getSelectAllCheckboxValue(),
                count: students?.count || 0,
                labelHeader: t('label.select-all-students'),
                labelItem: (row: Student) =>
                  t('label.select-student', { STUDENT: row.fullName }),
                onSelect: selection.onSelect,
                onSelectAll: selection.onSelectAll
              }
            : undefined
        }
        data={students?.list || []}
        columns={tableColumns}
        count={students?.count}
        totalCount={allStudentCount}
        {...pagination}
        tableWidth="100%"
        rightTopBar={
          user?.isSuperAdmin ? (
            <TableTopBarActions
              actions={bulkActions}
              selectedItemsLength={selection.selectedItems.length}
            />
          ) : null
        }
        caption={t('header.students')}
      />

      <ActivationLinkModal
        variant={activationLinkModalVariant}
        ids={currentStudent?.id ? [currentStudent.id] : selection.selectedItems}
        studentName={currentStudent?.fullName || ''}
        open={openActivationLinkModal}
        onSuccess={() => {
          refetch()
          selection.clearSelection()
        }}
        onOpenChange={value => {
          setOpenActivationLinkModal(value)
          if (!value) setCurrentStudent(null)
        }}
      />

      <ConfirmModal
        header={t('header.block-student')}
        subheader={t('help.want-to-block-student', {
          STUDENT: currentStudent?.fullName
        })}
        confirmButton={
          <Button variant="danger" onClick={handleBlock}>
            {t('button.block')}
          </Button>
        }
        open={openBlockConfirmModal}
        onOpenChange={setOpenBlockConfirmModal}
      />

      <ConfirmModal
        header={t('header.activate-student')}
        subheader={t('help.want-to-activate-student', {
          STUDENT: currentStudent?.fullName
        })}
        confirmButton={
          <Button onClick={handleActivate}>{t('button.activate')}</Button>
        }
        open={openActivateConfirmModal}
        onOpenChange={setOpenActivateConfirmModal}
      />

      <ArchiveStudentModal
        studentName={currentStudent?.fullName}
        open={openBulkArchiveConfirmModal}
        onOpenChange={setOpenBulkArchiveConfirmModal}
        ids={currentStudent ? [currentStudent.id] : selection.selectedItems}
        onArchive={handleBulkArchive}
      />
    </>
  )
}

type ParentsColumnProps = {
  parents: {
    id: string
    fullName: string
  }[]
}

const ParentsColumn = (props: ParentsColumnProps) => {
  const { t } = useTranslation('students')
  const [isExpanded, setIsExpanded] = useState(false)

  const parentList = props.parents.map(parent => parent.fullName)

  return props.parents.length ? (
    <TruncateElementsList
      expanded={isExpanded}
      onExpandedChange={setIsExpanded}
      elipsis={hiddenItemsCount => (
        <Tooltip
          text={takeRight(parentList, hiddenItemsCount).join(', ')}
          trigger={
            <Tag
              color="white"
              variant="with-background"
              label={`+${hiddenItemsCount}`}
              onClick={() => {
                setIsExpanded(true)
              }}
            />
          }
        />
      )}
    >
      {props.parents.map(parent => (
        <LinkText
          key={parent.id}
          className={styles.truncateElement}
          to="/students-and-classes/students/parents/$parentId/details"
          params={{
            parentId: parent.id
          }}
          truncateElement
        >
          {parent.fullName}
        </LinkText>
      ))}
    </TruncateElementsList>
  ) : (
    <span className={styles.textUnassigned}>{t('text.unassigned')}</span>
  )
}

export default StudentsView
